import type { FulfillmentOrderLine } from "../types";
import type { EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { roles, factors, unit, counts, EntryParseError, parseEntryDates, parseEntrySegments } from "./confirmationEntryPrimitives";
import { calculateFulfillmentLineAmount, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";

export const MISC_SHORTCUTS = ["单房差", "开票税金", "横幅", "快递费", "电话费", "蜀地资金池", "领队打车", "保险", "导游资金池", "运营费", "企业资金池", "非遗资金池"];

/** 本分类显式决定日期来源，日期范围不隐式参与计次数量。 */
function datePatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  if (!input.routeDays.length) throw new EntryParseError("当前行程没有出行日，请先到行程中补齐服务日期后再录入杂费", "range", "control");
  const range = [...new Set(input.routeDays.map(item => item.dayNo))].sort((a, b) => a - b).join(",");
  return parseEntryDates(input.routeDays, range, "服务", "control");
}

/** 本分类服务主体资格与结算主体分别保留。 */
function providerPatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  const supplier = input.provider && "id" in input.provider ? input.provider : null;
  if (supplier && "snapshotResourceType" in supplier) {
    if (supplier.snapshotResourceType !== "杂费") throw new EntryParseError(`服务主体“${supplier.name}”属于${supplier.snapshotResourceType}，不能用于杂费；请重新选择本类服务主体`, "provider");
  } else if (supplier) {
    if (!supplier.canBeFulfillmentSubject) throw new EntryParseError(`服务主体“${supplier.name}”不具备当前履约资格，请重新选择可履约的主体`, "provider");
  }
  return {
    fulfillmentSupplierId: supplier?.id ?? null,
    fulfillmentSupplierName: supplier?.name ?? null,
    settlementSupplierId: input.settlement?.id ?? null,
    settlementSupplierName: input.settlement?.name ?? null,
  };
}
/** 本分类明确资源类型和计价单位后校验完整行。 */
function basePatch(input: EntryParseBaseInput, name: string, quantity: string, count: string, price: string, quantityUnit: string, extra: Partial<FulfillmentOrderLine> = {}): Partial<FulfillmentOrderLine> {
  const amount = calculateFulfillmentLineAmount(quantity, count, price);
  const patch: Partial<FulfillmentOrderLine> = {
    resourceType: "杂费", itemNameSnapshot: name, itemSpecSnapshot: null,
    quantity, billingCount: count, unitPriceAmount: price, amount, unit: quantityUnit,
    pricingUnitType: "unit",
    reviewStatus: "confirmed", sourceType: "manual", status: "active", ...datePatch(input), ...providerPatch(input), ...extra,
  };
  const errors = validateFulfillmentLineDraft({ ...patch, id: input.allowZeroQuantity ? "existing-preview" : "local-preview" } as FulfillmentOrderLine);
  if (errors.length) throw new Error(errors.join("；"));
  return patch;
}
/** 解析本类单段原文，保留本类资源和计费约束。 */
function parseSegment(input: EntryParseBaseInput, text: string): Partial<FulfillmentOrderLine>[] {
  const raw = text.trim();
  const match = raw.match(new RegExp(`^(.+?)\\s*((?:${roles})?\\s*[\\d０-９].*)$`));
  if (!match) throw new Error("请填写费用名称及公式，保留实际人数、天数和单价");
  const name = match[1].trim().replace(/^[@＠]/, "");
  const roleMatch = match[2].trim().match(new RegExp(`^(${roles})\\s*(.*)$`));
  const role = (roleMatch?.[1] || "").replace("教师", "老师");
  const p = factors(roleMatch ? roleMatch[2] : match[2], undefined, input.allowZeroQuantity);
  let quantity = "1", count = "1", quantityUnit = "项", period = "次";
  if (p.length === 1 && role) { quantity = counts(input, role); quantityUnit = "人"; }
  else if (p.length > 1) {
    quantityUnit = "人";
    if (p.length === 2 && p[0].unit === "天" && role) { quantity = counts(input, role); count = p[0].value; period = "天"; }
    else { unit(p[0], ["人", "个"]); quantity = p[0].value; if (p.length === 3) { unit(p[1], ["天"]); count = p[1].value; period = "天"; } }
  }
  return [basePatch(input, name === "导服费" ? "导游服务费" : name, quantity, count, p.at(-1)!.value, quantityUnit, { travelerRole: role || null, itemSpecSnapshot: `${quantityUnit}×${period}` })];
}

/** 独立解析杂费文本，保留本类日期、计费和资源规则。 */
export function parseMiscEntry(input: EntryParseBaseInput): EntryParseResult {
  return parseEntrySegments(input.text, text => parseSegment(input, text), patch => [...(!patch.settlementSupplierId ? ["结算主体待设置"] : []), ...(!patch.fulfillmentSupplierId ? ["服务主体待核对"] : [])]);
}
